import App from 'srs.sharedcomponents/lib/esm/App'
import { useLocation, useHistory } from 'react-router-dom'
import { useEffect } from 'react'
import {
  ApplicationInsights,
  IPageViewTelemetry,
} from '@microsoft/applicationinsights-web'
import { env } from 'srs.sharedcomponents/lib/esm/api/env'
import { store, RootState } from 'srs.sharedcomponents/lib/esm/redux/store'
import {
  checkAndHandleAnonymousIdExpiry,
  uuidv4,
} from 'srs.sharedcomponents/lib/esm/utils/helpers'
import axios, { AxiosResponse } from 'axios'
import NetPriceIndicatorComponent from './components/molecules/NetPriceIndicator/NetPriceIndicator'
import RouterProvider from 'srs.sharedcomponents/lib/esm/components/context/router/RouterProvider'
import routesConfig from './components/routes/routes'
import { useTranslation } from 'react-i18next'
import { i18nNamespaces as namespace } from 'srs.sharedcomponents/lib/esm/i18n/i18n'
import coreSrsPrimaryOverride from './i18n/coreSrsPrimaryOverride.json'
import {
  useAppDispatch,
  useAppSelector,
} from 'srs.sharedcomponents/lib/esm/redux/hooks'
import {
  setAnonymousId,
  setIsShowAllMenuInCapitals,
} from 'srs.sharedcomponents/lib/esm/redux/slices/welcomeSlice'
import { setDisplayMessageFlag } from 'srs.sharedcomponents/lib/esm/redux/slices/cartSlice'
import { setFooterLinks } from 'srs.sharedcomponents/lib/esm/redux/slices/footerLinksSlice'

interface IAppWrapper {
  appInsights: ApplicationInsights
}

const AppWrapper = ({ appInsights }: IAppWrapper) => {
  const location = useLocation()
  const { i18n } = useTranslation()

  Object.entries(coreSrsPrimaryOverride).forEach(([locale, entity]) => {
    Object.entries(entity).forEach(([key, value]) => {
      if (typeof value === 'string') {
        i18n.addResource(locale, namespace.coreSrsPrimary, key, value)
      } else {
        console.error(
          `Value for key '${key}' in locale '${locale}' is not a string:`,
          value,
        )
      }
    })
  })
  const history = useHistory()
  const welcome = useAppSelector((state: RootState) => state?.welcome?.welcome)
  const dispatch = useAppDispatch()
  const { links } = useAppSelector((state) => state.footerLinks)
  const includeWebAccessibilityLink = false

  const publicRoutes = [
    '/login',
    '/PasswordRecovery',
    '/registration',
    '/contactus',
    '/cookies',
    '/warranty',
    '/opensources',
    '/getcorporateinformation',
    '/webaccessibility',
    '/Security/PasswordRecovery',
    '/Pages/PasswordRecovery.aspx',
    '/pagesSecurity/PasswordRecovery',
    '/ssoprocessing',
    '/sso',
    '/userActivation',
    '/newsletterprocessor',
    '/mfa-otp'
  ]

  useEffect(() => {
    const myRequestInterceptor = axios.interceptors.request.use((request) => {
      const { method, url, headers } = request

      if (request.headers) {
        request.headers['X-Correlation-Id'] = uuidv4()
      }

      if (request) {
        appInsights.trackEvent({
          name: 'API Request JLR',
          properties: {
            ApplicationName: 'jlr',
            RequestPath: url ?? '',
            Method: method ?? '',
            CorrelationId: headers ? headers['X-Correlation-Id'] : '',
          },
        })
      }

      return request
    })

    return () => {
      axios.interceptors.request.eject(myRequestInterceptor)
    }
  })

  useEffect(() => {
    const myResponseInterceptor = axios.interceptors.response.use(
      (response: AxiosResponse) => {
        const { config, status, statusText } = response
        const { method, url, headers } = config

        if (config) {
          appInsights.trackEvent({
            name: 'API Response JLR',
            properties: {
              ApplicationName: 'jlr',
              RequestPath: url ?? '',
              Method: method ?? '',
              CorrelationId: headers ? headers['X-Correlation-Id'] : '',
              StatusCode: status,
              StatusCodeText: statusText,
            },
          })
        }

        return response
      },
      (error) => {
        appInsights.trackEvent({
          name: 'API Error',
          properties: {
            message: error.message,
          },
        })
        if (!(error instanceof Error)) {
          error = new Error(String(error))
        }

        return Promise.reject(new Error(error))
      },
    )

    return () => {
      axios.interceptors.response.eject(myResponseInterceptor)
    }
  })

  useEffect(() => {
    //This will capitalize the left manu and top menu FOR JLR only
    dispatch(setIsShowAllMenuInCapitals(true))
  }, [welcome])

  useEffect(() => {
    //this will show message on product search for JLR only
    dispatch(setDisplayMessageFlag(true))
    
    // Call immediately on page load
    checkAndHandleAnonymousIdExpiry(dispatch, setAnonymousId)
    
    // Then call every 24 hours
    const interval = setInterval(() => {
      checkAndHandleAnonymousIdExpiry(dispatch, setAnonymousId)
    }, 86400000)
    
    return () => clearInterval(interval)
  }, [dispatch])

  useEffect(() => {
    const pageInfo: IPageViewTelemetry = {
      name: location?.pathname,
      uri: window?.location.href,
      isLoggedIn: !!store.getState().user?.user?.token,
    }
    if (env.APPLICATIONINSIGHTS_CONNECTION_STRING) {
      appInsights.trackPageView(pageInfo)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  useEffect(() => {
    const setCustomFooterLinks = () => {
      const footerLinks = includeWebAccessibilityLink
        ? links
        : links.filter((link) => link.id !== 'footerWebAccessibility')

      if (links.length !== footerLinks.length) {
        dispatch(setFooterLinks(footerLinks))
      }
    }
    setCustomFooterLinks()
  }, [links])

  useEffect(() => {
    // Route redirects mapping
    const redirects: Record<string, string> = {
      '/esst': '/pages/esst',
      '/atsp': '/pages/atsp',
      '/ToolOrganization': '/pages/ToolOrganization',
    }

    const currentPath = location?.pathname ?? ''
    const queryString = location?.search ?? ''

    // Redirect if path matches, preserving query parameters
    if (redirects[currentPath]) {
      history.replace(redirects[currentPath] + queryString)
      return
    }

    if (
      !store.getState().user?.user?.countryId &&
      !publicRoutes.find((publicRoute) =>
        location?.pathname.includes(publicRoute),
      ) &&
      location?.pathname !== '/mypreferences'
    ) {
      history.replace('/mypreferences')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  return (
    <RouterProvider routesConfig={routesConfig}>
      <App itemListPlaceholder={GetNetPriceComponent} />
    </RouterProvider>
  )
}

const GetNetPriceComponent = () => {
  return <NetPriceIndicatorComponent />
}

export default AppWrapper
